"use client";
import React, { useContext, useEffect, useState, useMemo } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import moment from "moment";
import { 
  Box, Button, MenuItem, Select, FormControl, InputLabel, 
  Typography, Grid, Paper, Avatar, Stack, IconButton
} from "@mui/material"; 
import { 
  EventNote, BarChart, Add 
} from "@mui/icons-material";
import MyContext from "../../context/MyContext";
import { QK } from "../../base/qk";
import {
  getBuildHistory,
  getScheduledRunVisits,
  getSubLocations,
  getUnAssignedVisits,
  updateVisits,
} from "../../api/services/scheduling";
import DropTask from "./DropTask";
import BuildScheduleModal from "../PopupModal/BuildScheduleModal";
import ShowScheduleModal from "../PopupModal/ShowScheduleModal";
import Loader from "../Loader/Loader";
import CreateRunModal from "../../screens/ClientsScreen/CreateRunModal";
import ScheduleHistory from "../WeeklyPlanner/ScheduleHistory";
import DayTimelineCalendar from "./DayTimelineCalendar";
import { ClientDayTimeline, ClientWeeklyTimeline, ExtendedTimelineView } from "./TimeLine";
import EmployeeCapacityReport from "./EmployeeCapacityReport";

const ItemTypes = { TASK: "card" };

const Job = ({ id, data }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TASK,
    item: { id, data },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }));
  return (
    <Paper
      ref={drag}
      elevation={0}
      sx={{
        p: 1.5, mb: 1, cursor: 'move', bgcolor: "#ffffff",
        opacity: isDragging ? 0.5 : 1, border: "1px solid #e2e8f0",
        borderRadius: '8px',
        transition: '0.2s',
        "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1", boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }
      }}
    >
      <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#1e293b' }}>{data.client}</Typography>
      <Typography sx={{ fontSize: '11px', color: '#64748b' }}>{data.visit_name}</Typography>
    </Paper>
  );
};

const TaskDropArea = ({ onDrop, label, tasks }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.TASK,
    drop: (item) => onDrop(item),
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  });
  return (
    <Box
      ref={drop}
      sx={{
        p: 1, borderRadius: 1, minHeight: '90px',
        bgcolor: isOver ? "#eff6ff" : "transparent",
        border: isOver ? "1px dashed #3b82f6" : "1px solid transparent",
        display: 'flex', flexWrap: 'wrap', gap: 1,
        position: 'relative'
      }}
    >
      {tasks.length === 0 && !isOver && (
        <Typography variant="caption" sx={{ color: '#94a3b8', m: 'auto' }}>{label}</Typography>
      )}
      {tasks.map((task) => (
        <Box key={task.id} sx={{ position: 'relative', zIndex: 1, '&:hover': { zIndex: 100 } }}>
          <DropTask task={task} type="schedule" />
        </Box>
      ))}
    </Box>
  );
};

const WeeklyPlanner = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { locationID } = useContext(MyContext);
  const [showBuildModal, setShowBuildModal] = useState(false);
  const [showRunModal, setRunShowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedSublocation, setSelectedSublocation] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [runType, setRunType] = useState("");
  const [currentView, setCurrentView] = useState("Jobs/Day/Runs");

  const { data: subLocations, isLoading: subLoading } = useQuery(
    ["subLocations", locationID, selectedDate], 
    () => getSubLocations(locationID, selectedDate), 
    { 
      enabled: !!locationID,
      refetchOnMount: true,
      staleTime: 0
    }
  );

  useEffect(() => {
    if (subLocations?.length > 0 && !selectedSublocation) {
      setSelectedSublocation(subLocations[0].id);
    }
  }, [subLocations, selectedSublocation]);

  const { data: buildHistory } = useQuery(
    [QK.buildHistory, locationID, selectedDate], 
    () => getBuildHistory(selectedDate, locationID), 
    { enabled: !!locationID }
  );
  
  const { data: runs, isLoading: runLoading } = useQuery(
    [QK.scheduledRunVisits, selectedDate, selectedSublocation, runType], 
    () => getScheduledRunVisits(selectedDate, selectedSublocation, runType), 
    { 
      enabled: !!selectedSublocation && currentView === "Jobs/Day/Runs",
      keepPreviousData: true 
    }
  );

  const sortedRuns = useMemo(() => {
    if (!runs) return [];
    return [...runs].sort((a, b) => {
      const aLen = a.tasks?.length || 0;
      const bLen = b.tasks?.length || 0;
      if (aLen > 0 && bLen === 0) return -1;
      if (aLen === 0 && bLen > 0) return 1;
      return 0;
    });
  }, [runs]);
  
  const { data: unAssignedTasks } = useQuery(
    [QK.unAssignedTasks, selectedSublocation], 
    () => getUnAssignedVisits(selectedSublocation), 
    { enabled: !!selectedSublocation }
  );

  const handleDrop = async (task, targetRunId) => {
    try {
      const response = await updateVisits(task.id, { is_on_right: true, run: targetRunId, date: selectedDate }, "schedule");
      if (response.status === 200) {
        enqueueSnackbar("Job Assigned", { variant: "success" });
        queryClient.invalidateQueries([QK.scheduledRunVisits]);
        queryClient.invalidateQueries([QK.unAssignedTasks]);
      }
    } catch (error) { enqueueSnackbar("Update Failed", { variant: "error" }); }
  };

  const isTimelineView = currentView.includes("Timeline") || currentView === "Employee Capacity Report";
  const isExtendedView = currentView.includes("Week/Timeline") || currentView.includes("Month/Timeline");
  const isDayTimeline = currentView === "Jobs/Day/Timeline" || currentView === "Employee/Day/Timeline";
  const viewType = useMemo(() => currentView.includes("Week") ? "week" : currentView.includes("Month") ? "month" : "day", [currentView]);

  const showLoader = (runLoading && !runs) || subLoading || (!selectedSublocation && !isTimelineView);

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", bgcolor: "#f8fafc", overflow: "hidden" }}>
        <Paper elevation={0} sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0", bgcolor: "#fff", zIndex: 1201 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ScheduleHistory 
                buildHistory={buildHistory} selectedDate={selectedDate} setSelectedDate={setSelectedDate} 
                sublocations={subLocations} selectedSublocation={selectedSublocation} setSelectedSublocation={setSelectedSublocation} 
              />
              <Button variant="contained" size="small" disableElevation startIcon={<EventNote />} onClick={() => setShowScheduleModal(true)}>
                Schedule
              </Button>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>View Mode</InputLabel>
                <Select value={currentView} onChange={(e) => setCurrentView(e.target.value)} label="View Mode">
                  <MenuItem value="Jobs/Day/Runs">Jobs/Day/Runs</MenuItem>
                  <MenuItem value="Jobs/Day/Timeline">Jobs/Day/Timeline</MenuItem>
                  <MenuItem value="Employee/Day/Runs">Employee/Day/Runs</MenuItem>
                  <MenuItem value="Employee/Day/Timeline">Employee/Day/Timeline</MenuItem>
                  <MenuItem value="Employee/Week/Timeline">Employee/Week/Timeline</MenuItem>
                  <MenuItem value="Employee/Month/Timeline">Employee/Month/Timeline</MenuItem>
                  <MenuItem value="Service User/Day/Timeline">Service User/Day/Timeline</MenuItem>
                  <MenuItem value="Service User/Week/Timeline">Service User/Week/Timeline</MenuItem>
                  <MenuItem value="Employee Capacity Report">Employee Capacity Report</MenuItem>
                </Select>
              </FormControl>
              <Button href="/reports" variant="outlined" size="small" color="inherit" startIcon={<BarChart />} sx={{ borderColor: '#cbd5e1' }}>
                Reports
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden', position: 'relative' }}>
          
          <Box sx={{ 
            width: isTimelineView ? 0 : 240, 
            transition: 'width 0.3s ease', 
            bgcolor: "#f1f5f9", 
            borderRight: isTimelineView ? "none" : "1px solid #e2e8f0", 
            overflow: "hidden",
            flexShrink: 0 
          }}>
            <Box sx={{ p: 2, width: 240 }}>
              <Typography variant="overline" sx={{ fontWeight: 900, color: '#475569', mb: 2, display: 'block', letterSpacing: 1 }}>
                UNASSIGNED ({unAssignedTasks?.length || 0})
              </Typography>
              <Box sx={{ overflowY: "auto", height: 'calc(100vh - 180px)' }}>
                {unAssignedTasks?.map((task) => <Job key={task.id} id={task.id} data={task} />)}
              </Box>
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", bgcolor: "#fff", overflow: "hidden" }}>
            <Box sx={{ p: 2, borderBottom: "1px solid #f1f5f9", bgcolor: "#fff" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ fontWeight: 800, fontSize: '15px', color: '#1e293b', minWidth: 150 }}>{currentView}</Typography>
                  {!isTimelineView && (
                    <Box sx={{ bgcolor: '#f1f5f9', p: 0.5, borderRadius: '8px', display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => setRunShowModal(true)} sx={{ bgcolor: '#fff', '&:hover': {bgcolor: '#fff'} }}><Add fontSize="small" /></IconButton>
                      <Button size="small" variant={runType === "" ? "contained" : "text"} onClick={() => setRunType("")} color="inherit" disableElevation sx={{ fontSize: '11px', width: 50 }}>All</Button>
                      <Button size="small" variant={runType === "am" ? "contained" : "text"} onClick={() => setRunType("am")} color="warning" disableElevation sx={{ fontSize: '11px', width: 50 }}>AM</Button>
                      <Button size="small" variant={runType === "pm" ? "contained" : "text"} onClick={() => setRunType("pm")} color="primary" disableElevation sx={{ fontSize: '11px', width: 50 }}>PM</Button>
                    </Box>
                  )}
                </Stack>
                <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#64748b' }}>
                  {moment(selectedDate).format("dddd, Do MMMM YYYY")}
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: "auto", position: 'relative' }}>
              {showLoader ? <Loader /> : (
                <Box sx={{ p: 2 }}>
                  {currentView === "Employee Capacity Report" && <EmployeeCapacityReport selectedDate={selectedDate} setSelectedDate={setSelectedDate} locationId={locationID} />}
                  {currentView === "Jobs/Day/Runs" && (
                    <Grid container spacing={2}>
                      {sortedRuns.map((run, index) => (
                        <Grid size={12} key={run.id || index} sx={{ zIndex: sortedRuns.length - index, position: 'relative' }}>
                          <Paper elevation={0} sx={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'visible', position: 'relative' }}>
                            <Box sx={{ width: 220, p: 2, bgcolor: '#f8fafc', borderRight: '1px solid #e2e8f0', flexShrink: 0 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: '14px', color: '#0f172a', mb: 1 }}>
                                {run.run_type === "am" ? "☀️" : "🌙"} {run.name}
                              </Typography>
                              <Stack spacing={0.5}>
                                {run.employee_names?.map((name, i) => (
                                  <Typography key={i} sx={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Avatar sx={{ width: 16, height: 16, fontSize: '8px' }}>{name[0]}</Avatar> {name}
                                  </Typography>
                                ))}
                              </Stack>
                            </Box>
                            <Box sx={{ flexGrow: 1, p: 1.5, bgcolor: '#fff' }}>
                              <TaskDropArea onDrop={(task) => handleDrop(task, run.id)} label={`Drop tasks for ${run.name}`} tasks={run.tasks || []} />
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  {isDayTimeline && <DayTimelineCalendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} />}
                  {currentView === "Service User/Week/Timeline" && <ClientWeeklyTimeline selectedDate={selectedDate} locationId={locationID} />}
                  {currentView === "Service User/Day/Timeline" && <ClientDayTimeline selectedDate={selectedDate} locationId={locationID} />}
                  {isExtendedView && <ExtendedTimelineView selectedDate={selectedDate} setSelectedDate={setSelectedDate} viewType={viewType} />}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <BuildScheduleModal show={showBuildModal} handleClose={() => setShowBuildModal(false)} />
      <ShowScheduleModal 
        show={showScheduleModal} handleClose={() => setShowScheduleModal(false)} 
        selectedDate={selectedDate} subLocations={subLocations} 
        setDate={setSelectedDate} setSelectedDate={setSelectedDate} setSelectedSublocation={setSelectedSublocation} 
      />
      <CreateRunModal area={selectedSublocation} show={showRunModal} handleClose={() => setRunShowModal(false)} />
    </DndProvider>
  );
};

export default WeeklyPlanner;